import { defineStore } from 'pinia'
import { entryRepository, type EntryDraft } from '@/repositories/entryRepository'
import { habitRepository, type HabitDraft } from '@/repositories/habitRepository'
import { settingsRepository } from '@/repositories/settingsRepository'
import type { AttachmentInput, Entry, Habit, HabitCheckin } from '@/types'

interface ToastMessage {
  id: number
  text: string
  tone: 'success' | 'error' | 'info'
}

export const useAppStore = defineStore('app', {
  state: () => ({
    entries: [] as Entry[],
    habits: [] as Habit[],
    checkins: [] as HabitCheckin[],
    settings: {} as Record<string, unknown>,
    loading: false,
    initialized: false,
    toasts: [] as ToastMessage[]
  }),
  getters: {
    activeHabits: (state) => state.habits.filter((habit) => !habit.archivedAt)
  },
  actions: {
    notify(text: string, tone: ToastMessage['tone'] = 'info') {
      const id = Date.now() + Math.random()
      this.toasts.push({ id, text, tone })
      window.setTimeout(() => { this.toasts = this.toasts.filter((item) => item.id !== id) }, 2800)
    },
    async initialize() {
      if (this.initialized || this.loading) return
      this.loading = true
      try {
        await this.reload()
        this.initialized = true
      } catch (error) {
        this.notify(error instanceof Error ? error.message : '本地数据读取失败', 'error')
      } finally {
        this.loading = false
      }
    },
    async reload() {
      const [entries, habits, checkins, settings] = await Promise.all([
        entryRepository.list(), habitRepository.list(true), habitRepository.listCheckins(), settingsRepository.all()
      ])
      this.entries = entries
      this.habits = habits
      this.checkins = checkins
      this.settings = settings
    },
    async saveEntry(draft: EntryDraft, additions: AttachmentInput[] = [], removed: string[] = []) {
      try {
        const saved = await entryRepository.save(draft, additions, removed)
        await this.reload()
        this.notify('已经好好收下了', 'success')
        return saved
      } catch (error) {
        this.notify(error instanceof Error ? error.message : '保存失败，请稍后重试', 'error')
        throw error
      }
    },
    async deleteEntry(id: string) {
      try {
        await entryRepository.softDelete(id)
        await this.reload()
        this.notify('记录已删除', 'success')
      } catch (error) {
        this.notify('删除失败，请稍后重试', 'error')
        throw error
      }
    },
    async saveHabit(draft: HabitDraft) {
      try {
        await habitRepository.save(draft)
        await this.reload()
        this.notify('习惯已保存', 'success')
      } catch (error) {
        this.notify('习惯保存失败', 'error')
        throw error
      }
    },
    async archiveHabit(id: string, archived: boolean) {
      await habitRepository.archive(id, archived)
      await this.reload()
      this.notify(archived ? '习惯已归档' : '习惯已启用', 'success')
    },
    async toggleCheckin(habitId: string, date?: string) {
      try {
        const checked = await habitRepository.toggleCheckin(habitId, date)
        await this.reload()
        this.notify(checked ? '打卡完成' : '已撤销打卡', 'success')
      } catch (error) {
        this.notify('打卡失败，请稍后重试', 'error')
        throw error
      }
    },
    async setSetting<T>(key: string, value: T) {
      try {
        await settingsRepository.set(key, value)
        this.settings = { ...this.settings, [key]: value }
        this.notify('设置已保存', 'success')
      } catch (error) {
        this.notify('设置保存失败', 'error')
        throw error
      }
    }
  }
})
